import { type FC } from 'react';
import { products } from '@/mocks/products';

interface BestSellersStripProps {
  onProductClick: (link: string) => void;
}

const bestSellerIds = [7, 9, 3, 5, 10, 19, 22, 35, 27, 30];

const BestSellersStrip: FC<BestSellersStripProps> = ({ onProductClick }) => {
  const bestSellers = products.filter((p) => bestSellerIds.includes(Number(p.id)));

  return (
    <section className="px-4 py-10 bg-gradient-to-r from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl shadow-md">
              <i className="ri-fire-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight">Best Sellers</h2>
              <p className="text-sm text-gray-500">Most loved by our cat community</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full text-xs font-bold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            Updated Weekly
          </div>
        </div>

        {/* Scrollable Cards */}
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
          {bestSellers.map((product, index) => (
            <div
              key={product.id}
              onClick={() => onProductClick(product.link)}
              className="flex-shrink-0 w-[200px] md:w-[220px] snap-start bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.03] cursor-pointer group"
            >
              {/* Rank Badge */}
              <div className="relative">
                <img
                  src={product.image}
                  alt={product.name}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://public.readdy.ai/ai/img_res/3bef5e4cde10ebd7944becfba93f5226.jpg';
                  }}
                  className="w-full h-36 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-2 left-2 w-7 h-7 flex items-center justify-center bg-amber-500 text-white text-xs font-black rounded-full shadow-md">
                  #{index + 1}
                </div>
                {product.badge && (
                  <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                    product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                    product.badge === 'BESTSELLER' ? 'bg-amber-500 text-white' :
                    product.badge === 'TOP RATED' ? 'bg-teal-500 text-white' :
                    'bg-gray-700 text-white'
                  }`}>
                    {product.badge}
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1 mb-1.5">
                  <i className="ri-medal-line text-amber-500 text-xs"></i>
                  <span className="text-xs font-semibold text-amber-600">Highly Rated on Chewy</span>
                </div>
                <h4 className="font-bold text-gray-800 text-xs leading-snug line-clamp-2 mb-2 min-h-[32px]">
                  {product.name}
                </h4>
                <div className="flex items-center justify-between">
                  <span className="text-base font-black text-teal-600">${product.price}</span>
                  <button className="bg-teal-500 hover:bg-teal-600 text-white text-xs font-bold px-3 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer">
                    Shop
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BestSellersStrip;